import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { Translate } from 'react-redux-i18n';
import { EntityWithComparison, MetricKey } from '../types';
import { getMetricValueForEntity, getSharedCurrency } from '../utils';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { EntityCard } from './EntityCard';

// Helper function to split currency into major and minor parts for styling
const splitCurrencyParts = (formatted: string): { major: string; minor: string } => {
  const match = formatted.match(/^(.*?)([\.,])(\d{2})$/);
  if (!match) {
    return { major: formatted, minor: '' };
  }
  const [, major, separator, cents] = match;
  return { major, minor: `${separator}${cents}` };
};

// Currency metrics that should show decimal places
const CURRENCY_METRICS: MetricKey[] = [
  'raisedAmount',
  'feeCollected',
  'donationAverageAmount',
  'saleRaisedAmount',
  'donationRaisedAmount',
  'fundraiserAverageRaisedAmount',
  'ambassadorFundraisedAmount'
];

// Helper to format value based on metric type
const formatMetricValue = (amount: number, metric: MetricKey, currency?: string): React.ReactNode => {
  const isCurrency = CURRENCY_METRICS.includes(metric);

  if (isCurrency) {
    const formatted = CurrencyHelper.formatCurrency(amount, currency, { alwaysShowDecimals: true });
    const { major, minor } = splitCurrencyParts(formatted);

    return minor ? (
      <>
        {major}
        <span className="franchise-amount-minor">{minor}</span>
      </>
    ) : formatted;
  } else {
    // For counts and other non-currency metrics, show whole numbers
    return amount.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
};

export interface YearGroupProps {
  year: number;
  entities: EntityWithComparison[];
  includeOffline: boolean;
  showAllMetrics: boolean;
  isSystemAdmin: boolean;
  isAnimating: boolean;
  expandAll: boolean;
  selectedMetric: MetricKey | null;
}

export const YearGroup: React.FC<YearGroupProps> = ({
  year,
  entities,
  includeOffline,
  showAllMetrics,
  isSystemAdmin,
  isAnimating,
  expandAll,
  selectedMetric
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Sync with expandAll prop
  useEffect(() => {
    setIsExpanded(expandAll);
  }, [expandAll]);

  if (!entities || entities.length === 0) {
    return null;
  }

  const displayYear = year === 0 ? 'No Year' : year.toString();
  
  // Determine which metric to display (default to raisedAmount if none selected)
  const metricToDisplay = selectedMetric || 'raisedAmount';

  // Calculate total for the selected metric
  const totalValue = entities.reduce((sum, entity) => {
    const entityValue = getMetricValueForEntity(entity, metricToDisplay, includeOffline);
    return sum + entityValue;
  }, 0);
  // Only surface a currency-denominated total when the whole group shares one
  // currency; summing mixed currencies under an arbitrary symbol is misleading.
  const groupCurrency = getSharedCurrency(entities);
  const metricIsCurrency = CURRENCY_METRICS.includes(metricToDisplay);
  const canShowTotal = !metricIsCurrency || groupCurrency !== undefined;

  const isSingleEntity = entities.length === 1;

  return (
    <div className="franchise-year-group">
      {/* Year Header - Clickable */}
      <div 
        className="franchise-year-header clickable"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="franchise-year-header-left">
          {isExpanded ? (
            <ChevronDown className="franchise-year-chevron" />
          ) : (
            <ChevronRight className="franchise-year-chevron" />
          )}
          <Calendar className="franchise-year-icon" />
          <span className="franchise-year-value">{displayYear}</span>
          <span className="franchise-year-count">
            ({entities.length}{' '}
            {entities.length === 1 ? (
              <Translate value="franchise.hub.year.entity-singular" />
            ) : (
              <Translate value="franchise.hub.year.entity-plural" />
            )}
            )
          </span>
        </div>
        {/* Only show total if more than 1 entity and the group currency is unambiguous */}
        {!isSingleEntity && canShowTotal && (
          <span className="franchise-year-total">
            {formatMetricValue(totalValue, metricToDisplay, groupCurrency)}
          </span>
        )}
      </div>

      {/* Collapsed View - Compact List */}
      {!isExpanded && (
        <div className="franchise-entity-compact-list">
          {entities.map(entity => {
            const entityValue = getMetricValueForEntity(entity, metricToDisplay, includeOffline);
            const yoyChange = entity.yoyChanges?.[metricToDisplay];
            const performanceUrl =
              entity.linkedEntityType === 'campaign'
                ? `/campaigns/performance/${entity.id}`
                : `/events/performance/${entity.id}`;
            
            return (
              <div key={entity.id} className="franchise-entity-compact-item">
                <a href={performanceUrl} className="franchise-entity-compact-name">
                  {entity.name}
                </a>
                {yoyChange !== undefined && (
                  <span className={`franchise-entity-compact-yoy ${yoyChange >= 0 ? 'positive' : 'negative'}`}>
                    {yoyChange >= 0 ? '↑' : '↓'}
                    {Math.abs(yoyChange).toFixed(1)}%
                  </span>
                )}
                <span className="franchise-entity-compact-amount">
                  {formatMetricValue(entityValue, metricToDisplay, entity.currency)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Expanded View - Full Cards in Grid */}
      {isExpanded && (
        <div className="franchise-entity-cards-grid">
          {entities.map(entity => (
            <EntityCard
              key={entity.id}
              entity={entity}
              includeOffline={includeOffline}
              showAllMetrics={showAllMetrics}
              isSystemAdmin={isSystemAdmin}
              isAnimating={isAnimating}
              selectedMetric={selectedMetric}
            />
          ))}
        </div>
      )}
    </div>
  );
};
